/**
 * Living inside the desktop shell.
 *
 * WHO DECIDES WHAT TO NOTIFY
 *   The renderer does. It is the only side that knows who you are, which
 *   changes you have already seen, and whether you are looking at the file
 *   right now. The shell only knows how to raise a notification. That division
 *   is why the web build and the desktop build run the same code: in a browser
 *   the bridge is simply absent and the hook does nothing.
 *
 * WHAT WE DO NOT NOTIFY ABOUT
 *   - your own changes (the single commonest way a notification system becomes
 *     noise and then gets muted)
 *   - anything at or below the sequence number you had already read
 *   - the initial catch-up after being away: one summary, not forty alerts
 */
import { useEffect, useRef } from 'react'
import { describeChange, parseMentions, type Change, type Comment } from '@lattice/core'
import { useWorkspace, personOf } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { onChange } from '@/store/sync'
import { api } from '@/store/api'
import { toHash } from '@/store/ui'

export interface LatticeBridge {
  notify(request: { title: string; body: string; deepLink?: string }): Promise<boolean>
  setBadge(count: number): Promise<boolean>
  platform(): Promise<{ platform: string; version: string; notifications: boolean }>
  reveal(path: string): Promise<boolean>
  onOpen(handler: (path: string) => void): () => void
  /** Folder synchronisation. Absent in a browser, and in an older shell. */
  folder?: FolderBridge
}

/* ------------------------------------------------------------------ */
/* The folder bridge                                                   */
/* ------------------------------------------------------------------ */

/**
 * WHAT THIS IS
 *   The only way the interface can touch the filesystem. Everything below is
 *   declared here and implemented in the Electron shell; the renderer never
 *   sees a file handle, a path outside the linked folder, or Node.
 *
 * WHY EVERY MANIFEST IS A LIST OF PAIRS
 *   A Map does not survive Electron's structured-clone IPC boundary — it
 *   arrives as `{}`. So manifests cross the bridge as pairs and are rebuilt
 *   into a core `Manifest` on arrival, in one place (`manifestFrom`).
 *
 * DATA DEFINITIONS
 *
 *   A FolderFacts is { sha, size }.
 *   Interpretation: the SHA-256 of a file's bytes as it is on disk, and its
 *   byte length. The same facts core's `FileFacts` carries, unbranded because
 *   the shell has no reason to know about branded types.
 *
 *   A ManifestPairs is [path, FolderFacts][], the paths project-relative and
 *   separated by "/" on every platform (the shell converts Windows separators
 *   at its own boundary, exactly as `parsePath` does on the server).
 *
 *   A FolderAction is "download" | "delete-local" | "upload" | "delete-remote"
 *   | "record-only" — core's Action minus "nothing", which is never sent.
 *   Interpretation: the single operation to perform for one path.
 *
 *   WHICH OF THEM REACH `apply`: only "download" and "delete-local". The other
 *   two are requests to the SERVER, and the renderer performs them itself (see
 *   `applyRemote` in store/sync-folder.ts): it holds the session, the replica
 *   and the retry logic. The shell answers "done" to an upload without
 *   contacting the server, so a renderer that handed them over and believed
 *   the answer would record a file as sent that never left the machine — and
 *   the next comparison would offer to pull the stale copy over the top of it.
 *
 *   A FolderOperation is one FolderAction applied to one path, carrying
 *   whatever the shell cannot work out for itself.
 *
 *   An OperationResult is { path, ok, error? }: what actually happened. The
 *   shell reports every operation, including the ones that failed, because a
 *   half-applied sync that claims to have succeeded is how a manifest starts
 *   lying about what is on disk.
 */

export interface FolderFacts {
  readonly sha: string
  readonly size: number
}

export type ManifestPairs = readonly (readonly [string, FolderFacts])[]

/** What `readLink` knows about a project: where it is, and the last pivot. */
export interface FolderLinkRecord {
  readonly dir: string
  readonly manifest: ManifestPairs
}

export interface FolderScan {
  readonly files: ManifestPairs
  /** Files whose hash could not be read — open in SolidWorks, or unreadable. */
  readonly skippedHashes: number
}

export type FolderAction = 'download' | 'delete-local' | 'upload' | 'delete-remote' | 'record-only'

export interface FolderOperation {
  readonly path: string
  readonly action: FolderAction
  /**
   * The content hash this operation is expected to move, so the shell can
   * verify what it wrote (download) or confirm what it read (upload) rather
   * than trusting the path alone.
   */
  readonly sha?: string
  readonly size?: number
  /**
   * For "download" only: the hash this machine held when the plan was made,
   * or '' if the plan found nothing there. The shell checks the disk against
   * it and refuses to overwrite a file that changed in between — a person can
   * spend a minute reading the change list, and a save can land inside it.
   */
  readonly expect?: string
  /**
   * For "download" only: where the bytes are. Resolved by the renderer through
   * /api/files/download, because signing a blob URL is the server's business
   * and the renderer already knows how to ask for one.
   */
  readonly url?: string
}

export interface OperationResult {
  readonly path: string
  readonly ok: boolean
  readonly error?: string
}

/**
 * A progress report from a long-running bridge call.
 * Interpretation: "we are `done` of `total` files into `phase`". Bytes are
 * optional because a scan knows its file count long before it knows its size.
 */
export interface FolderProgress {
  readonly phase: 'scanning' | 'applying'
  readonly done: number
  readonly total: number
  readonly bytesDone?: number
  readonly bytesTotal?: number
  readonly path?: string
}

export interface FolderBridge {
  /** Open the native folder picker. Undefined when the person cancels. */
  chooseFolder(): Promise<string | undefined>
  /** Remember dir as this project's folder; answers with the manifest found there. */
  linkFolder(projectId: string, dir: string): Promise<ManifestPairs>
  readLink(projectId: string): Promise<FolderLinkRecord | undefined>
  unlink(projectId: string): Promise<void>
  /** Hash everything in the folder. The slow one: ~900 files for this team. */
  scan(projectId: string, dir: string): Promise<FolderScan>
  apply(
    projectId: string, dir: string, operations: readonly FolderOperation[],
  ): Promise<readonly OperationResult[]>
  /** Write the new pivot beside the files, so the next session can compare. */
  writeManifest(projectId: string, dir: string, files: ManifestPairs): Promise<void>
  revealFolder(dir: string): Promise<void>
  /**
   * Send one local file's bytes to a URL the server issued.
   *
   * Optional on this side: a browser tab has no shell at all, and an older
   * desktop build predates this method. The caller checks before using it and
   * reports honestly when it is missing, rather than recording a file as sent
   * that never left the machine.
   */
  sendFile?(input: {
    projectId: string; dir: string; path: string
    url: string; method: 'PUT' | 'POST'; headers: Record<string, string>; sha: string
  }): Promise<{ ok: boolean; bytes?: number; error?: string }>
  /**
   * OPTIONAL. Abandon whatever is running for this project.
   *
   * The renderer stops LISTENING the moment somebody presses Cancel, and an
   * apply simply stops sending chunks — but a scan is one long call, and
   * without this it keeps reading hundreds of files off the disk to produce an
   * answer that has already been thrown away.
   */
  cancel?(projectId: string): Promise<void>
  /**
   * OPTIONAL. Progress while a scan or an apply is running.
   * The interface works without it — an apply is driven in chunks and counts
   * itself — but a scan is one long call, so without this the person watches a
   * spinner rather than a number. Implement it if you can.
   */
  onProgress?(handler: (event: FolderProgress) => void): () => void
}

/** The bridge, or undefined when running in an ordinary browser tab. */
export const desktop = (): LatticeBridge | undefined =>
  (globalThis as { lattice?: LatticeBridge }).lattice

/** True when running inside the Electron shell. */
export const isDesktop = (): boolean => desktop() !== undefined

/**
 * The folder half of the bridge, or undefined when there is no filesystem to
 * reach — a browser tab, or a shell built before folder sync existed.
 * folderBridge : -> FolderBridge|undefined
 */
export const folderBridge = (): FolderBridge | undefined => desktop()?.folder

/** True when this build can mirror a project into a folder. */
export const canSyncFolders = (): boolean => folderBridge() !== undefined

/** Above this many new rows at once we summarise instead of listing. */
const SUMMARY_THRESHOLD = 4

/**
 * Raise native notifications for changes this person has not seen.
 * useDesktopNotifications : -> void
 */
export function useDesktopNotifications(): void {
  const lastNotified = useRef(0)

  useEffect(() => {
    const bridge = desktop()
    if (!bridge) return

    // Never announce history that arrived before this window opened.
    lastNotified.current = Math.max(lastNotified.current, useWorkspace.getState().readMark, useWorkspace.getState().cursor)

    const unsubscribe = onChange(() => {
      const { changes, comments, roster, user, readMark } = useWorkspace.getState()
      if (!user) return
      const since = Math.max(lastNotified.current, readMark)

      const freshChanges = changes.filter(c => c.seq > since && c.authorId !== user.id)
      const freshComments = comments.filter(c => c.seq > since && c.authorId !== user.id && !c.deletedAt)
      if (freshChanges.length === 0 && freshComments.length === 0) return

      lastNotified.current = useWorkspace.getState().cursor

      // A mention is always worth its own notification — it is addressed to you.
      for (const comment of freshComments) {
        const mentioned = comment.mentions.includes(user.id) || parseMentions(comment.body).includes(user.id)
        if (!mentioned) continue
        void bridge.notify({
          title: `${personOf(roster, comment.authorId).name} mentioned you`,
          body: comment.body.slice(0, 200),
          deepLink: toHash({ projectId: comment.projectId, view: 'comments', openPath: comment.path }),
        })
      }

      const others = freshComments.filter(c => !c.mentions.includes(user.id))
      announce(bridge, freshChanges, others, roster)

      const unseen = useWorkspace.getState().changes.filter(c => c.seq > readMark && c.authorId !== user.id).length
      void bridge.setBadge(unseen)
    })

    return unsubscribe
  }, [])
}

/** One notification per change when there are few; one summary when there are many. */
function announce(
  bridge: LatticeBridge,
  changes: Change[],
  comments: Comment[],
  roster: ReturnType<typeof useWorkspace.getState>['roster'],
): void {
  if (changes.length + comments.length === 0) return

  if (changes.length + comments.length > SUMMARY_THRESHOLD) {
    const people = [...new Set([...changes, ...comments].map(c => personOf(roster, c.authorId).name))]
    void bridge.notify({
      title: `${changes.length + comments.length} updates in Lattice`,
      body: `from ${people.slice(0, 3).join(', ')}${people.length > 3 ? ` and ${people.length - 3} others` : ''}`,
      deepLink: changes[0] ? toHash({ projectId: changes[0].projectId, view: 'activity' }) : undefined,
    })
    return
  }

  for (const change of changes) {
    void bridge.notify({
      title: 'Lattice',
      body: describeChange(change, personOf(roster, change.authorId).name),
      deepLink: toHash({ projectId: change.projectId, view: 'files', openPath: change.path }),
    })
  }
  for (const comment of comments) {
    void bridge.notify({
      title: `${personOf(roster, comment.authorId).name} commented`,
      body: comment.body.slice(0, 200),
      deepLink: toHash({ projectId: comment.projectId, view: 'comments', openPath: comment.path }),
    })
  }
}

/**
 * Follow a notification or a lattice:// link into the right file.
 * useDeepLinks : -> void
 */
export function useDeepLinks(): void {
  useEffect(() => {
    const bridge = desktop()
    if (!bridge) return
    return bridge.onOpen(path => {
      window.location.hash = path.startsWith('#') ? path : `#${path.startsWith('/') ? '' : '/'}${path}`
    })
  }, [])
}

/**
 * Keep the dock badge in step with what has been read.
 * Marking read is debounced and pushed to the server so the count is the same
 * on every machine this person uses.
 * useReadTracking : -> void
 */
export function useReadTracking(): void {
  const cursor = useWorkspace(s => s.cursor)
  const view = useUi(s => s.view)

  useEffect(() => {
    // Looking at the activity feed is what "reading" means; browsing files is not.
    if (view !== 'activity') return
    const timer = setTimeout(() => {
      useWorkspace.getState().setReadMark(cursor)
      void api.readMark(cursor).catch(() => undefined)
      void desktop()?.setBadge(0)
    }, 1200)
    return () => clearTimeout(timer)
  }, [cursor, view])
}
